import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { applyBrandTokens, deriveBrandTokens, type ResolvedTheme } from "./brand";

export type ThemeMode = "system" | "light" | "dark";

/** Where a visitor's light/dark choice is kept. The staff and member apps share an origin, so each passes its own key. */
export const DEFAULT_THEME_KEY = "clubcore.theme";

function readStoredMode(key: string): ThemeMode {
  try {
    const value = localStorage.getItem(key);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === "system" ? systemTheme() : mode;
}

/** Call before first render so the page never flashes the wrong theme. */
export function applyInitialTheme(storageKey: string = DEFAULT_THEME_KEY): void {
  document.documentElement.dataset.theme = resolve(readStoredMode(storageKey));
}

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children, storageKey = DEFAULT_THEME_KEY }: { children: ReactNode; storageKey?: string }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode(storageKey));
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(mode));

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setResolved(resolve(mode));
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [mode]);

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      if (next === "system") localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, next);
    } catch {
      /* storage unavailable: the choice lasts for this visit */
    }
  }, [storageKey]);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}

/** Applies a club's accent color for the current theme; renders nothing. Unmounting restores the defaults. */
export function BrandAccent({ color }: { color: string | null | undefined }) {
  const { resolved } = useTheme();
  useEffect(() => {
    applyBrandTokens(document.documentElement, deriveBrandTokens(color, resolved));
    return () => applyBrandTokens(document.documentElement, null);
  }, [color, resolved]);
  return null;
}
